/**
 * ConnectionDiagram — diagrama «desde el punto de vista de X».
 *
 * Pinta la cadena física de un elemento de la ficha en la que estés:
 * sus puertos a la izquierda y, a la derecha, la ruta completa del puerto
 * elegido (equipo → toma de pared → patch panel → switch → uplink), con el
 * tipo/color de cable de cada tramo.
 *
 * La fuente es `GET /api/cabling/element-chain`, que devuelve una cadena por
 * puerto con los extremos ya expandidos. `kind`:
 *   panel | switch | ap | server | controller
 *
 * Es el mismo componente para las cinco fichas: no hay cinco diagramas.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './ConnectionDiagram.css';

const PORT_ICON = {
  panel_port: '🧩',
  switch_port: '🔀',
  wall_outlet: '🔌',
  component_port: '🖥️',
  wifi_ap_port: '📶',
  ip_phone_port: '☎️',
  printer_port: '🖨️',
  workstation_port: '💻',
  router_iface: '🌐',
  firewall_iface: '🛡️',
  wall_panel: '🔌',
};

const PORT_KIND_LABEL = {
  panel_port: 'Patch panel',
  switch_port: 'Switch',
  wall_outlet: 'Toma de pared',
  component_port: 'Equipo',
  wifi_ap_port: 'AP',
  ip_phone_port: 'Teléfono IP',
  printer_port: 'Impresora',
  workstation_port: 'Puesto',
  router_iface: 'Router',
  firewall_iface: 'Firewall',
  wall_panel: 'Toma de pared',
};

const cableLabel = (link) => {
  const parts = [];
  if (link?.cable_type) parts.push(String(link.cable_type).toUpperCase());
  if (link?.length_m) parts.push(`${link.length_m} m`);
  return parts.join(' · ') || 'cable';
};

export default function ConnectionDiagram({ kind, id, companyId, title = 'Diagrama de conexiones' }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !kind || !id) return;
    setLoading(true); setError(null);
    try {
      const res = await api.getElementChain(kind, id, companyId);
      setData(res.data || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [companyId, kind, id]);

  useEffect(() => { load(); }, [load]);

  const chains = useMemo(() => data?.chains || [], [data]);
  const connected = useMemo(() => chains.filter(c => c.connected), [chains]);

  // Al cargar se abre la primera cadena con cable: es la que se quiere ver.
  useEffect(() => {
    if (!chains.length) { setSelectedKey(null); return; }
    setSelectedKey(prev => {
      if (prev && chains.some(c => `${c.port_type}:${c.port_id}` === prev)) return prev;
      const first = chains.find(c => c.connected) || chains[0];
      return `${first.port_type}:${first.port_id}`;
    });
  }, [chains]);

  const selected = chains.find(c => `${c.port_type}:${c.port_id}` === selectedKey) || null;

  const steps = useMemo(() => {
    if (!selected) return [];
    const links = selected.links || [];
    if (!links.length) {
      return [{
        port: {
          port_type: selected.port_type,
          label: selected.label,
          element_name: title,
        },
      }];
    }
    const out = [{ port: links[0].from }];
    links.forEach(link => out.push({ port: link.to, cable: link }));
    return out;
  }, [selected, title]);

  const desconectar = async (link) => {
    if (!window.confirm('¿Desconectar este cable?')) return;
    setBusy(true); setError(null);
    try {
      await api.deleteCablingLink(link.kind, link.linked_id, companyId);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const grupos = useMemo(() => {
    // La controladora agrupa los puertos de todos sus APs.
    const out = [];
    chains.forEach(chain => {
      const group = chain.group || null;
      let bucket = out.find(g => g.name === group);
      if (!bucket) { bucket = { name: group, chains: [] }; out.push(bucket); }
      bucket.chains.push(chain);
    });
    return out;
  }, [chains]);

  return (
    <div className="cdg-wrap">
      <div className="cdg-head">
        <span className="cdg-title">{title}</span>
        <span className="cdg-counts">
          {chains.length} {chains.length === 1 ? 'puerto' : 'puertos'} · {connected.length} con cable
        </span>
        <span className="cdg-spacer" />
        {error && <span className="cdg-error">{error}</span>}
        <button type="button" className="cdg-btn" onClick={load} disabled={loading || busy}>
          {loading ? 'Cargando…' : 'Recargar'}
        </button>
        <button type="button" className="cdg-btn" onClick={() => nav('/cabling/designer')}>
          Abrir en el diseñador ↗
        </button>
      </div>

      {!loading && !chains.length && (
        <div className="cdg-empty">
          Este elemento no tiene puertos todavía. Se crean en la pestaña de puertos.
        </div>
      )}

      {chains.length > 0 && (
        <div className="cdg-body">
          {/* Vista 1: todos los puertos */}
          <div className="cdg-ports">
            {grupos.map(grupo => (
              <React.Fragment key={grupo.name || 'todos'}>
                {grupo.name && <div className="cdg-group">{grupo.name}</div>}
                {grupo.chains.map(chain => {
                  const key = `${chain.port_type}:${chain.port_id}`;
                  const far = chain.links?.[0]?.to;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`cdg-port${selectedKey === key ? ' is-active' : ''}${chain.connected ? ' is-wired' : ''}`}
                      onClick={() => setSelectedKey(key)}
                    >
                      <span className="cdg-port-label">{chain.label}</span>
                      <span className={`cdg-port-far${chain.connected ? '' : ' is-free'}`}>
                        {chain.connected
                          ? `${far?.element_name || '—'}${far?.label ? ` · ${far.label}` : ''}`
                          : 'sin cable'}
                      </span>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            {data?.truncated && (
              <div className="cdg-note">
                Se muestran los primeros {chains.length} puertos de {data.port_count}.
              </div>
            )}
          </div>

          {/* Vista 2: la cadena del puerto elegido */}
          <div className="cdg-chain">
            {!selected && <div className="cdg-empty">Elige un puerto.</div>}
            {selected && (
              <>
                <div className="cdg-chain-head">
                  <b>{selected.label}</b>
                  <span>{selected.connected ? 'cadena completa' : 'puerto libre'}</span>
                </div>
                <div className="cdg-flow">
                  {steps.map((step, index) => (
                    <React.Fragment key={`${step.port?.port_type}-${step.port?.port_id}-${index}`}>
                      {index > 0 && (
                        <div className="cdg-cable">
                          <span className="cdg-cable-line" />
                          <span className="cdg-cable-txt">
                            <span
                              className="cdg-cable-dot"
                              style={{ background: step.cable?.cable_color || '#64748b' }}
                            />
                            {cableLabel(step.cable)}
                          </span>
                          <button
                            type="button"
                            className="cdg-cable-x"
                            title="Desconectar este cable"
                            disabled={busy}
                            onClick={() => desconectar(step.cable)}
                          >✕</button>
                        </div>
                      )}
                      <div
                        className={`cdg-step${index === 0 ? ' is-focus' : ''}`}
                        title={`${PORT_KIND_LABEL[step.port?.port_type] || step.port?.port_type || ''}${
                          step.port?.room ? ` · ${step.port.room}` : ''
                        }`}
                      >
                        <span className="cdg-step-icon">
                          {PORT_ICON[step.port?.port_type] || '•'}
                        </span>
                        <span className="cdg-step-txt">
                          <b>{step.port?.element_name || '—'}</b>
                          <span>{step.port?.label || PORT_KIND_LABEL[step.port?.port_type] || ''}</span>
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                {!selected.connected && (
                  <div className="cdg-note">
                    Este puerto no tiene ningún cable. Se cablea desde el diseñador de cableado
                    o con el botón de la ficha del puerto.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
